"use client";
import Link from "next/link";
// framer-motion removed for performance
import { Code2, LineChart, Paintbrush, Video, Megaphone, ArrowLeft } from "lucide-react";
import OptimizedImage from "@/components/ui/OptimizedImage";
import Image from "next/image";

const ICONS = { Code2, LineChart, Paintbrush, Video, Megaphone };
// Map tool names to imported SVG assets placed in /public/SVGs
// Only some courses have matching SVGs; others gracefully fallback to text only.
const TOOL_SVGS = {
  // Web / General
  HTML: "/SVGs/google.svg", // placeholder; replace with dedicated HTML icon if added
  CSS: "/SVGs/google.svg", // placeholder
  JS: "/SVGs/google.svg", // placeholder
  React: "/SVGs/google.svg", // placeholder
  "Next.js": "/SVGs/google.svg", // placeholder
  Git: "/SVGs/google.svg", // placeholder
  Vercel: "/SVGs/google.svg", // placeholder
  // Web3 / Trading
  Etherscan: "/SVGs/etherscan.svg",
  DEXs: "/SVGs/dex-screener.svg",
  TradingView: "/SVGs/tradingview.svg",
  Bitcoin: "/SVGs/bitcoin.svg",
  // Design
  Figma: "/SVGs/illustrator.svg", // placeholder
  Illustrator: "/SVGs/illustrator.svg",
  Photoshop: "/SVGs/photoshop.svg",
  "Google Fonts": "/SVGs/fonts.svg",
  // Video / Motion
  "Premiere Pro": "/SVGs/premier.svg",
  "After Effects": "/SVGs/after-effects.svg",
  "DaVinci Resolve": "/SVGs/premier.svg", // placeholder
  CapCut: "/SVGs/capcut.svg",
  // Marketing
  Canva: "/SVGs/canva.svg",
  "Meta Ads": "/SVGs/meta.svg",
  GA4: "/SVGs/google.svg",
  GSC: "/SVGs/google.svg",
  Notion: "/SVGs/notion.svg", // placeholder
};
// End tool icon mapping

export default function CourseDetailClient({ course, detail }) {
  const Icon = ICONS[course.icon] ?? Megaphone;

  // Simple intersection reveal (CSS class only) could be added later if needed

  return (
  <section className="relative overflow-hidden" style={{
        "--course-gradient": detail.theme.gradient,
        "--course-ring": detail.theme.ring,
      }}>
        {/* Orange ambience */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 -translate-x-1/2 -top-40 w-[760px] h-[760px] opacity-20" style={{ background: "var(--course-ring)" }} />
          <div className="absolute right-0 top-1/3 w-[520px] h-[520px] rotate-45 rounded-full blur-3xl opacity-35" style={{ background: "var(--course-gradient)" }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20">
          <div className="flex justify-between">
            <Link href="/#courses" className="inline-flex items-center gap-2 text-sm font-medium text-brand hover:opacity-90">
              <ArrowLeft className="w-4 h-4"/>
              Back to Courses
            </Link>
             <a href="/enroll" className="inline-flex items-center gap-2 rounded-full px-4 h-10 text-xs font-semibold text-white bg-brand-gradient hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand transition">Enroll Now</a>
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.12fr_0.88fr] gap-10 items-start">
            {/* Hero card */}
            <div className="order-2 lg:order-1 relative rounded-3xl  p-5" style={{background: 'var(--blue-surface)'}}>
              <div className="absolute inset-0 opacity-40 mix-blend-overlay" style={{ background: "var(--course-ring)" }} />
              <div className="relative">
                <div className="flex items-start gap-5">

                <div className="w-16 h-16  rounded-2xl flex  items-center justify-center border border-[color-mix(in_srgb,var(--brand)_45%,transparent)] bg-[linear-gradient(135deg,rgba(var(--brand-rgb),0.18),rgba(var(--brand-rgb),0.05))] shadow-[0_0_0_1px_color-mix(in_srgb,var(--brand)_35%,transparent),0_10px_24px_-8px_rgba(0,0,0,0.35)]">
                  <Icon className="w-8 h-8 text-brand" />
                </div>
                <div className="flex-1">


                  <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-brand-gradient">{course.title}</h1>
                  <p className="mt-1 text-[11px] underline uppercase tracking-wider text-brand/80">{course.duration} • {detail.weeks.length} weeks</p>
                </div>
                </div>
                <div className="flex-1 ">
                  <p className="mt-3 text-base text-white/70 max-w-2xl">{detail.long}</p>
                  <div className="mt-5 flex flex-wrap gap-3 text-xs">
                    <span className="rounded-full   font-bold border-[color-mix(in_srgb,var(--brand)_40%,var(--blue-border))] text-brand">{course.level}</span>
                    <span className="rounded-full px-3   font-bold border-[color-mix(in_srgb,var(--brand)_40%,var(--blue-border))] text-brand">{course.duration}</span>
                    {detail.highlights.map(h => (
                      <span key={h} className="rounded-full font-bold    border-[color-mix(in_srgb,var(--brand)_40%,var(--blue-border))] text-brand/90">{h}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Enroll card / Hero image */}
            <aside className="order-1 lg:order-2 rounded-3xl overflow-hidden" style={{background: 'var(--blue-surface)'}}>
              <div className="relative aspect-[16/9] sm:aspect-[16/9] lg:h-full min-h-56 rounded-3xl">
                <OptimizedImage
                  src={course.image}
                  alt={course.title}
                  fill
                  sizes="(max-width: 1024px) 90vw, 40vw"
                  quality={80}
                  className="object-cover"
                  priority
                  fetchPriority="high"
                />
              </div>
            </aside>
          </div>

          {/* Weekly breakdown */}
          <div className="mt-14" style={{background: 'transparent'}}>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-brand mb-6">Weekly Breakdown</h2>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {detail.weeks.map((w, i) => (
                <div key={w.title} className="relative rounded-2xl p-5 border border-[color-mix(in_srgb,var(--brand)_30%,var(--blue-border))] bg-[#102130]">
                  <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity" style={{ background: "var(--course-ring)" }} />
                  <div className="relative">
                    <p className="text-xs font-semibold tracking-wide uppercase text-brand/90">Week {i+1}</p>
                    <p className="mt-1 text-brand font-medium">{w.title}</p>
                    <ul className="mt-2 space-y-1 text-sm text-white/70">
                      {w.topics.map(t => (<li key={t} className="flex items-start gap-2"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand" />{t}</li>))}
                    </ul>
                    <p className="mt-3 text-xs text-brand/90"><span className="font-semibold text-brand">Mini project:</span> {w.project}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tools & Instructors */}
          <div className="mt-14">
            <h2 className="text-2xl font-semibold tracking-tight text-brand mb-4">Tools</h2>
            <div className="flex flex-wrap gap-3 text-sm">
              {course.tools.map(name => {
                const svg = TOOL_SVGS[name] || (name.toLowerCase().includes('dex') ? TOOL_SVGS.DEXs : undefined);
                return (
                  <span key={name} className="rounded-full flex gap-2 items-center pl-2 pr-3 py-1.5 bg-brand-gradient border border-[color-mix(in_srgb,var(--brand)_35%,var(--blue-border))] shadow-[0_4px_14px_-6px_rgba(0,0,0,0.4)]">
                    {/* {svg && (
                      <span className="relative inline-flex w-6 h-6 items-center justify-center rounded-md bg-black/20 ring-1 ring-white/10 overflow-hidden">
                        <OptimizedImage
                          src={svg}
                          alt={`${name} icon`}
                          width={24}
                          height={24}
                          className="object-contain"
                        />
                      </span>
                    )} */}
                    <span className="font-medium text-white/90 text-xs tracking-wide">{name}</span>
                  </span>
                );
              })}
            </div>
            <div className="mt-10">
              <h3 className="text-xl font-semibold tracking-tight text-brand mb-3">Instructors</h3>
              <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                {detail.instructors.map(p => (
                  <li key={p.name} className="group relative rounded-2xl p-4 border border-[color-mix(in_srgb,var(--brand)_25%,var(--blue-border))] bg-[linear-gradient(135deg,rgba(255,255,255,0.035),rgba(255,255,255,0.02))] overflow-hidden">
                    <div className="flex items-start gap-3">
                      <div className="relative  rounded-xl overflow-hidden ring-1 ring-white/10 bg-black/30 flex items-center justify-center text-[10px] uppercase tracking-wide text-white/40">
                        {p.image ? (
                          <Image
                            src={p.image}
                            alt={p.name}
                            width={60}
                            height={60}
                                          />
                        ) : (
                          <span>{p.name.split(' ').map(w=>w[0]).slice(0,2).join('')}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-brand font-medium truncate">{p.name}</p>
                        <p className="text-white/70 text-xs leading-snug">{p.role}</p>
                        {p.bio && <p className="mt-2 text-[11px] leading-relaxed text-white/55 line-clamp-3">{p.bio}</p>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
  </section>
  );
}
